import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Wind, Heart, Leaf, Eye, Users, Shield, Check } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_IMAGES = {
  hero: "https://images.unsplash.com/photo-1545389336-cf090694435e?w=1920",
  vision: "https://images.unsplash.com/photo-1600334089648-b0d9d3028eb2?w=800",
  values: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800"
};

export const FilosofiaPage = () => {
  const [content, setContent] = useState({});

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content`);
        const pageContent = {};
        response.data.forEach(item => {
          if (item.page === 'filosofia') {
            pageContent[item.section] = item;
          }
        });
        setContent(pageContent);
      } catch (error) {
      }
    };
    fetchContent();
  }, []);

  const heroContent = content.hero || {};
  const visionContent = content.vision || {};
  const valuesContent = content.values || {};

  const pillars = [
    {
      icon: Wind,
      title: 'Respiración',
      description: 'La respiración es el primer acto de vida y el puente entre cuerpo y mente. A través de la haloterapia y de las prácticas que ofrecemos, facilitamos entornos que favorecen una respiración más profunda, consciente y reparadora.'
    },
    {
      icon: Heart,
      title: 'Regulación',
      description: 'Entendemos el bienestar desde la perspectiva del sistema nervioso. Diseñamos experiencias y espacios que ayudan a reducir la sobrecarga sensorial y a promover estados de calma real, sostenibles en el tiempo.'
    },
    {
      icon: Eye,
      title: 'Presencia',
      description: 'Promovemos una cultura del momento presente. Creemos en el valor de parar, sentir y habitar el espacio con atención. El bienestar no surge de hacer más, sino de hacer con mayor conciencia.'
    }
  ];

  const commitments = [
    'Ofrecer un entorno seguro, inclusivo y respetuoso para adultos y niños.',
    'Informar de manera responsable sobre los beneficios de la haloterapia como complemento natural al cuidado de la salud.',
    'Fomentar hábitos de autocuidado sostenibles y conscientes.',
    'Crear espacios accesibles para personas con alta sensibilidad o necesidades sensoriales específicas.',
    'Colaborar en la construcción de una cultura del bienestar más pausada, humana y respetuosa con los ritmos individuales.'
  ];

  return (
    <div className="bg-halo-cream" data-testid="filosofia-page">
      {/* Hero */}
      <section className="relative py-24 bg-halo-charcoal overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src={heroContent.image_url || DEFAULT_IMAGES.hero}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="text-halo-brown font-medium tracking-widest text-sm">
              {heroContent.subtitle || 'NUESTRA ESENCIA'}
            </span>
            <h1 className="font-heading text-5xl md:text-6xl text-halo-cream mt-4 mb-6">
              {heroContent.title || 'Nuestra Filosofía'}
            </h1>
            <p className="text-halo-cream/80 text-lg leading-relaxed">
              {heroContent.text || 'En HALO creemos que el bienestar no es un lujo, sino una necesidad esencial en una sociedad que vive cada vez más deprisa.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main philosophy */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="font-heading text-4xl text-halo-charcoal">
                Un espacio de <span className="text-halo-brown italic">pausa consciente</span>
              </h2>
              <p className="text-halo-text leading-relaxed text-lg">
                Nuestro proyecto nace de la convicción de que respirar, detenerse y 
                habitar el momento presente no deberían ser excepciones en la rutina 
                diaria, sino parte natural de ella.
              </p>
              <p className="text-halo-text leading-relaxed">
                Vivimos en un entorno marcado por la inmediatez, la sobreestimulación 
                y la desconexión corporal. En este contexto, HALO se crea como un 
                espacio de pausa consciente, donde el ritmo disminuye y el cuerpo 
                puede recuperar su equilibrio natural.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src={visionContent.image_url || DEFAULT_IMAGES.vision}
                alt="Interior HALO"
                className="rounded-2xl w-full h-[400px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Three pillars */}
      <section className="py-24 bg-halo-surface">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl text-halo-charcoal mb-4">
              Tres pilares fundamentales
            </h2>
            <p className="text-halo-text max-w-2xl mx-auto">
              Nuestra filosofía se basa en tres principios que guían todo lo que hacemos
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {pillars.map((pillar, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.15 }}
                viewport={{ once: true }}
                className="bg-halo-cream p-8 rounded-2xl border border-halo-taupe/10 text-center"
              >
                <div className="w-20 h-20 bg-halo-brown/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <pillar.icon size={36} className="text-halo-brown" />
                </div>
                <h3 className="font-heading text-2xl text-halo-charcoal mb-4">
                  {pillar.title}
                </h3>
                <p className="text-halo-text text-sm leading-relaxed">
                  {pillar.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src={valuesContent.image_url || DEFAULT_IMAGES.values}
                alt="Compromiso HALO"
                className="rounded-2xl w-full h-[450px] object-cover"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <span className="text-halo-brown font-medium tracking-widest text-sm">
                  NUESTRO COMPROMISO
                </span>
                <h2 className="font-heading text-4xl text-halo-charcoal mt-4">
                  Más que un centro de haloterapia
                </h2>
              </div>
              
              <p className="text-halo-text leading-relaxed">
                HALO no es únicamente un centro de haloterapia; es un proyecto 
                comprometido con el bienestar de la sociedad.
              </p>

              <ul className="space-y-4">
                {commitments.map((commitment, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-halo-sage/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={14} className="text-halo-sage" />
                    </div>
                    <span className="text-halo-text text-sm leading-relaxed">{commitment}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values quote */}
      <section className="py-24 bg-halo-brown">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <p className="font-heading text-2xl md:text-3xl text-halo-cream leading-relaxed">
              Creemos que el verdadero lujo es poder detenerse.
            </p>
            <p className="font-heading text-2xl md:text-3xl text-halo-cream leading-relaxed">
              Que el verdadero avance es aprender a regularnos.
            </p>
            <p className="font-heading text-2xl md:text-3xl text-halo-cream leading-relaxed">
              Y que el verdadero bienestar comienza cuando decidimos vivir 
              de forma más consciente y menos acelerada.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Final message */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <p className="text-halo-text text-lg leading-relaxed">
              En HALO trabajamos para que cada persona que nos visite no solo 
              respire mejor, sino que también recuerde la importancia de estar presente.
            </p>
            <Link
              to="/bonos"
              className="inline-flex items-center gap-2 bg-halo-brown text-halo-cream px-8 py-4 rounded-full font-medium hover:bg-halo-charcoal transition-colors"
              data-testid="filosofia-reservar-btn"
            >
              Comprar bono
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FilosofiaPage;
